import { useState } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export default function TripCalendar({ startDate, endDate, onDateChange, minNights = 3, maxNights = 21 }) {
  const initial = parseDate(startDate) || new Date(2026, 8, 1); // Sep 2026
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const [selecting, setSelecting] = useState(null); // 'start' | 'end' | null

  const start = parseDate(startDate);
  const end = parseDate(endDate);
  const nights = start && end ? Math.round((end - start) / (1000 * 60 * 60 * 24)) : 0;

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = (day) => {
    const clicked = new Date(viewYear, viewMonth, day);
    const clickedStr = formatDate(clicked);

    if (!selecting || selecting === 'start' || !start) {
      onDateChange(clickedStr, null);
      setSelecting('end');
    } else {
      // Selecting end date
      if (clicked <= start) {
        // Clicked before start — reset
        onDateChange(clickedStr, null);
        setSelecting('end');
      } else {
        const diffDays = Math.round((clicked - start) / (1000 * 60 * 60 * 24));
        if (diffDays >= minNights && diffDays <= maxNights) {
          onDateChange(startDate, clickedStr);
          setSelecting(null);
        } else if (diffDays < minNights) {
          // Snap to min nights
          const minEnd = new Date(start);
          minEnd.setDate(minEnd.getDate() + minNights);
          onDateChange(startDate, formatDate(minEnd));
          setSelecting(null);
        } else {
          // Snap to max nights
          const maxEnd = new Date(start);
          maxEnd.setDate(maxEnd.getDate() + maxNights);
          onDateChange(startDate, formatDate(maxEnd));
          setSelecting(null);
        }
      }
    }
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="bg-navy-light rounded-2xl border border-brand-ink/10 p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-brand-ink/65 uppercase font-bold tracking-wider">Trip Dates</div>
          <div className="text-sm text-brand-ink/80 mt-0.5">
            {start && end ? (
              <>
                <span className="font-semibold text-white">{start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                <span className="text-brand-ink/55 mx-1.5">&rarr;</span>
                <span className="font-semibold text-white">{end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                <span className="text-teal font-bold ml-2">{nights} nights</span>
              </>
            ) : start ? (
              <span className="text-gold">Pick your return date</span>
            ) : (
              <span className="text-brand-ink/65">Pick your departure date</span>
            )}
          </div>
        </div>
        {start && end && (
          <button
            onClick={() => { onDateChange(null, null); setSelecting('start'); }}
            className="text-xs text-brand-ink/55 hover:text-brand-ink/70 transition"
          >
            Reset
          </button>
        )}
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-brand-ink/10 text-brand-ink/70 transition">&larr;</button>
        <span className="text-sm font-semibold">{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-brand-ink/10 text-brand-ink/70 transition">&rarr;</button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-0.5">
        {DAYS.map(d => (
          <div key={d} className="text-center text-[10px] font-semibold text-brand-ink/55 py-1">{d}</div>
        ))}

        {/* Empty cells before first day */}
        {Array.from({ length: firstDay }, (_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {/* Day cells */}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const date = new Date(viewYear, viewMonth, day);
          const dateStr = formatDate(date);
          const isPast = date < today;
          const isStart = start && dateStr === startDate;
          const isEnd = end && dateStr === endDate;
          const isInRange = start && end && date > start && date < end;
          const isToday = date.getTime() === today.getTime();

          return (
            <button
              key={day}
              onClick={() => !isPast && handleDayClick(day)}
              disabled={isPast}
              className={`
                h-10 sm:h-9 rounded-lg text-sm font-medium transition-all relative
                ${isPast ? 'text-white/10 cursor-not-allowed' : 'hover:bg-brand-ink/10 cursor-pointer'}
                ${isStart ? 'bg-teal text-navy font-bold ring-2 ring-teal/50 z-10' : ''}
                ${isEnd ? 'bg-teal text-navy font-bold ring-2 ring-teal/50 z-10' : ''}
                ${isInRange ? 'bg-teal/15 text-teal' : ''}
                ${!isStart && !isEnd && !isInRange && !isPast ? 'text-brand-ink/80' : ''}
                ${isToday && !isStart && !isEnd ? 'ring-1 ring-white/20' : ''}
              `}
            >
              {day}
              {isStart && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 text-[8px] text-navy font-bold">GO</span>}
              {isEnd && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 text-[8px] text-navy font-bold">BACK</span>}
            </button>
          );
        })}
      </div>

      {/* Quick select */}
      <div className="flex flex-wrap gap-2">
        <QuickDate label="5 nights" nights={5} start={start} onSelect={(s, e) => { onDateChange(s, e); setSelecting(null); }} />
        <QuickDate label="7 nights" nights={7} start={start} onSelect={(s, e) => { onDateChange(s, e); setSelecting(null); }} />
        <QuickDate label="10 nights" nights={10} start={start} onSelect={(s, e) => { onDateChange(s, e); setSelecting(null); }} />
        <QuickDate label="14 nights" nights={14} start={start} onSelect={(s, e) => { onDateChange(s, e); setSelecting(null); }} />
      </div>

      {/* Info */}
      {selecting === 'end' && (
        <div className="text-xs text-teal/70 animate-fade-in">
          Tap your return date on the calendar (min {minNights} nights)
        </div>
      )}
    </div>
  );
}

function QuickDate({ label, nights, start, onSelect }) {
  const handleClick = () => {
    const s = start || new Date(2026, 8, 1);
    const e = new Date(s);
    e.setDate(e.getDate() + nights);
    onSelect(formatDate(s), formatDate(e));
  };

  return (
    <button
      onClick={handleClick}
      className="px-4 py-2.5 sm:px-3 sm:py-1.5 bg-brand-ink/5 border border-brand-ink/10 rounded-lg text-xs text-brand-ink/70 hover:bg-brand-ink/10 hover:text-brand-ink/80 transition min-h-[44px] sm:min-h-0"
    >
      {label}
    </button>
  );
}
